import styled from "styled-components/macro";
import { BoxContent, Box } from "./Box";
import { Pencil } from "./Icons";
import { MediaMessage, SmsMessage, VoicemailMessage } from "./Message";
import Stack from "./Stack";
import MessageComposerSidePanel from "./MessageComposerSidePanel";
import { IconButton } from "./Button";
import Tooltip from "./Tooltip";
import useMoment from "../hooks/use-moment";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  fetchContactScheduledMessages,
  fetchGroupScheduledMessages,
  Message,
} from "../api/messages";
import { useEffect } from "react";
import Loader from "./Loader";

type MultiRecipientMessage = Message & { contactIds: string[] };

function groupMultiRecipientMessages(
  messages: Message[]
): MultiRecipientMessage[] {
  messages = messages.slice().sort((a, b) => {
    return Date.parse(a.time) - Date.parse(b.time);
  });

  const returned: MultiRecipientMessage[] = [];

  const push = (message: Message) =>
    returned.push({
      ...message,
      contactIds: [message.contactId],
    });

  for (let message of messages) {
    if (!returned.length) {
      push(message);
      continue;
    }

    const lastMessage = returned[returned.length - 1];

    if (
      message.messageType === lastMessage.messageType &&
      message.time === lastMessage.time
    ) {
      lastMessage.contactIds.push(message.contactId);
    } else push(message);
  }

  return returned;
}

export function ContactScheduledMessages({ contactId }: { contactId: string }) {
  let { data } = useQuery({
    queryKey: ["messages", "scheduled", { contactId }],
    queryFn: () => fetchContactScheduledMessages(contactId),
    select: (queryData) => queryData.data,
  });
  const messages = data!.filter(
    (m) =>
      !m.deliveredAt && m.failedAt === null && Date.parse(m.time) > Date.now()
  );

  return (
    <>
      {messages.length === 0 ? (
        <EmptyState>There are no scheduled messages</EmptyState>
      ) : (
        <Container>
          <Stack>
            {messages.map((message) => (
              <MessageEditor key={message.id} message={message} />
            ))}
          </Stack>
        </Container>
      )}
    </>
  );
}

export function GroupScheduledMessages({ groupId }: { groupId: string }) {
  const {
    data,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["messages", "scheduled", { groupId }],
    queryFn: ({ pageParam }) => fetchGroupScheduledMessages(groupId, pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta || !lastPage.meta.hasMore) return;
      return {
        cursor: lastPage.data[lastPage.data.length - 1].id,
        direction: "forward",
      };
    },
  });
  const messages = data!.pages.flatMap((page) => page.data) || [];

  /* HACK:
   * Allowing users to interact with a "multi recipient" message that has only
   * partly loaded can be destructive - we need to load every message to make
   * sure edits are applied to every intended recipient.
   */
  useEffect(() => {
    if (hasNextPage && !isLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isLoading, isFetchingNextPage]);

  const groupedMessages = groupMultiRecipientMessages(
    [...messages].filter(
      (m) =>
        !m.deliveredAt && m.failedAt === null && Date.parse(m.time) > Date.now()
    )
  );

  if (hasNextPage) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return groupedMessages.length === 0 ? (
    <EmptyState>There are no scheduled messages</EmptyState>
  ) : (
    <Container>
      <Stack>
        {groupedMessages.map((message) => (
          <MessageEditor key={message.id} message={message} />
        ))}
      </Stack>
    </Container>
  );
}

function MessageEditor({
  message,
}: {
  message: Message & { contactIds?: string[] };
}) {
  const moment = useMoment();
  let metadata = `Scheduled for ${moment(message.time).format("llll")}`;
  metadata += message.contactIds
    ? ` to ${message.contactIds.length} contact${
        message.contactIds.length === 1 ? "" : "s"
      }`
    : "";

  let messageElement;
  if (message.media.length) {
    messageElement = (
      <div>
        {message.media.map((mediaContent, index) => (
          <MediaMessage
            mediaType={mediaContent.type}
            mediaUrl={mediaContent.url}
            metadata={metadata}
            stackedWithNextMessage={
              !!message.body || index < message.media.length - 1
            }
          />
        ))}
        {message.body && (
          <SmsMessage metadata={metadata}>{message.body}</SmsMessage>
        )}
      </div>
    );
  } else if (message.voicemailId) {
    messageElement = (
      <VoicemailMessage voicemailId={message.voicemailId} metadata={metadata} />
    );
  } else {
    messageElement = (
      <SmsMessage metadata={metadata}>{message.body}</SmsMessage>
    );
  }

  return (
    <Box>
      <BoxContent style={{ padding: "10px" }} dark>
        <Inline>
          {messageElement}
          <MessageComposerSidePanel
            messageId={message.id}
            contactIds={message.contactIds || [message.contactId]}
            trigger={({ ref, show }) => (
              <Tooltip label="Edit">
                <IconButton
                  variant="link"
                  onClick={show}
                  ref={ref}
                  style={{ marginRight: "10px" }}
                >
                  <Pencil />
                </IconButton>
              </Tooltip>
            )}
          />
        </Inline>
      </BoxContent>
    </Box>
  );
}

const Container = styled.div`
  margin: auto;
  max-width: 600px;
  height: 100%;
  width: 100%;
  & > * {
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  color: #676767;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
`;

const Inline = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  ${IconButton} {
    position: relative;
    top: 8px;
  }
`;
